import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Calendar, Search, RefreshCw, Loader2, Power, Trash2, Mail, Zap,
  Bell, FileText, Building2, ChevronDown, ChevronUp, CheckCircle2, XCircle,
  Download, Sparkles, Pencil, ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';

export const KIND_META = {
  report:       { label: 'Report',       Icon: FileText, color: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30' },
  export:       { label: 'Export',       Icon: Download, color: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30' },
  alert:        { label: 'Alert',        Icon: Bell,     color: 'bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/30' },
  anomaly_scan: { label: 'Anomaly scan', Icon: Zap,      color: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30' },
  briefing:     { label: 'Briefing',     Icon: Sparkles, color: 'bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30' },
};

const KIND_ORDER = ['report', 'export', 'alert', 'anomaly_scan', 'briefing'];
const GRID = 'grid-cols-[28px_minmax(0,1fr)_104px_minmax(120px,160px)_minmax(120px,160px)_172px]';

function fmtTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function KindBadge({ kind }) {
  const meta = KIND_META[kind] || KIND_META.report;
  const { Icon } = meta;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-medium ${meta.color}`}>
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
}

// Map a stored frequency string to one of the four simple cadence modes.
function detectMode(freq) {
  const f = String(freq || '').toLowerCase();
  if (f.startsWith('hour') || f.includes('every') && f.includes('hour')) return 'hourly';
  if (f.startsWith('week')) return 'weekly';
  if (f.startsWith('month')) return 'monthly';
  if (f.startsWith('dai') || f.startsWith('day')) return 'daily';
  return null;
}

// ─── Edit dialog (type-aware) ──────────────────────────────────────────────
function EditScheduleDialog({ item, onClose, onSave }) {
  const kind = item?.kind;
  const spec = item?.schedule_spec || {};
  const curMode = detectMode(item?.frequency) || spec.mode || spec.type || null;

  const [name, setName] = useState(item?.name || '');
  const [recipients, setRecipients] = useState((item?.recipients || []).join('\n'));
  // 'keep' means "don't change the cadence" — prevents silently clobbering it.
  const [cadMode, setCadMode] = useState('keep');
  const [hour, setHour] = useState((spec.time?.hour ?? parseInt(String(spec.time || '').split(':')[0], 10)) || 8);
  const [minute, setMinute] = useState((spec.time?.minute ?? parseInt(String(spec.time || '').split(':')[1], 10)) || 0);
  const [everyHours, setEveryHours] = useState(spec.hourly?.hours ?? spec.interval ?? 1);
  const [checkFreq, setCheckFreq] = useState(item?.check_frequency || 'daily');
  const [intervalHours, setIntervalHours] = useState(item?.interval_hours || 24);
  const [cadence, setCadence] = useState(item?.cadence && item.cadence !== 'off' ? item.cadence : 'weekly');
  const [saving, setSaving] = useState(false);

  const showName = kind === 'report' || kind === 'export' || kind === 'alert';
  const showSpecBuilder = kind === 'report' || kind === 'export';

  const handleSave = async () => {
    const patch = {};
    if (showName) patch.name = name.trim();
    patch.recipients = recipients.split(/[\n,]+/).map(r => r.trim()).filter(Boolean);
    if (showSpecBuilder && cadMode !== 'keep') {
      patch.cadence = cadMode === 'hourly'
        ? { mode: 'hourly', hours: Math.max(1, parseInt(everyHours, 10) || 1) }
        : { mode: cadMode, hour: parseInt(hour, 10) || 0, minute: parseInt(minute, 10) || 0 };
    }
    if (kind === 'alert') patch.check_frequency = checkFreq;
    if (kind === 'anomaly_scan') patch.interval_hours = Math.max(1, parseInt(intervalHours, 10) || 1);
    if (kind === 'briefing') patch.cadence = cadence;
    setSaving(true);
    try {
      await onSave(item, patch);
      onClose();
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'bg-background border-border text-foreground rounded-xl';

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-card border-border text-foreground max-w-md" data-testid="edit-schedule-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <Pencil className="w-4 h-4 text-blue-500" /> Edit schedule
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {showName && (
            <div>
              <Label className="text-xs text-muted-foreground">Name</Label>
              <Input value={name} onChange={e => setName(e.target.value)} className={`mt-1 ${inputCls}`} data-testid="edit-name-input" />
            </div>
          )}

          {showSpecBuilder && (
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Cadence</Label>
              <Select value={cadMode} onValueChange={setCadMode}>
                <SelectTrigger className={inputCls} data-testid="edit-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="keep">Keep current{curMode ? ` (${item.frequency})` : ''}</SelectItem>
                  <SelectItem value="hourly">Hourly</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
              {cadMode === 'hourly' && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">every</span>
                  <Input type="number" min="1" max="24" value={everyHours} onChange={e => setEveryHours(e.target.value)} className={`w-20 ${inputCls}`} data-testid="edit-every-hours" />
                  <span className="text-muted-foreground">hour(s)</span>
                </div>
              )}
              {(cadMode === 'daily' || cadMode === 'weekly' || cadMode === 'monthly') && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">at</span>
                  <Input type="number" min="0" max="23" value={hour} onChange={e => setHour(e.target.value)} className={`w-16 ${inputCls}`} data-testid="edit-hour" />
                  <span className="text-muted-foreground">:</span>
                  <Input type="number" min="0" max="59" value={minute} onChange={e => setMinute(e.target.value)} className={`w-16 ${inputCls}`} data-testid="edit-minute" />
                  <span className="text-muted-foreground text-xs">UTC</span>
                </div>
              )}
            </div>
          )}

          {kind === 'alert' && (
            <div>
              <Label className="text-xs text-muted-foreground">Check frequency</Label>
              <Select value={checkFreq} onValueChange={setCheckFreq}>
                <SelectTrigger className={`mt-1 ${inputCls}`} data-testid="edit-check-freq"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Hourly</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {kind === 'anomaly_scan' && (
            <div>
              <Label className="text-xs text-muted-foreground">Scan every (hours)</Label>
              <Input type="number" min="1" value={intervalHours} onChange={e => setIntervalHours(e.target.value)} className={`mt-1 ${inputCls}`} data-testid="edit-interval-hours" />
            </div>
          )}

          {kind === 'briefing' && (
            <div>
              <Label className="text-xs text-muted-foreground">Cadence</Label>
              <Select value={cadence} onValueChange={setCadence}>
                <SelectTrigger className={`mt-1 ${inputCls}`} data-testid="edit-cadence"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="text-xs text-muted-foreground">Recipients (one per line)</Label>
            <Textarea value={recipients} onChange={e => setRecipients(e.target.value)} rows={3} placeholder="name@company.com" className={`mt-1 ${inputCls} font-mono text-xs`} data-testid="edit-recipients" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl" data-testid="edit-cancel-btn">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="rounded-xl bg-blue-500 hover:bg-blue-600 text-white" data-testid="edit-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ScheduleManagerView({
  title, description, Icon = Calendar, testidPrefix = 'schedules',
  loadFn, toggleFn, deleteFn, editFn, showTenant = false,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [busy, setBusy] = useState({});
  const [expanded, setExpanded] = useState({});
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('all');
  const [enabledOnly, setEnabledOnly] = useState(false);
  const qRef = useRef('');
  qRef.current = q;

  // `load` intentionally does NOT depend on `q` — searching happens only when
  // the user hits Apply/Enter, so we don't fire an API call per keystroke.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await loadFn({ q: qRef.current.trim() || undefined, kind: kind === 'all' ? undefined : kind, enabled_only: enabledOnly });
      setData(res);
      setForbidden(false);
    } catch (err) {
      if (err.status === 403) { setForbidden(true); }
      else { toast.error(err.message || 'Failed to load schedules'); }
    } finally {
      setLoading(false);
    }
  }, [kind, enabledOnly, loadFn]);

  useEffect(() => { load(); }, [load]);

  const onToggle = async (item) => {
    const key = `${item.kind}:${item.schedule_id}`;
    setBusy(b => ({ ...b, [key]: 'toggle' }));
    try {
      const res = await toggleFn(item.kind, item.schedule_id);
      toast.success(`${item.name} is now ${res.enabled ? 'enabled' : 'disabled'}`);
      await load();
    } catch (err) {
      toast.error(err.message || 'Toggle failed');
    } finally {
      setBusy(b => { const c = { ...b }; delete c[key]; return c; });
    }
  };

  const onDelete = async (item) => {
    const warn = item.kind === 'briefing'
      ? `Turn off the executive summary briefing? Recipients: ${(item.recipients || []).join(', ') || '(none)'}.`
      : `Delete "${item.name}"${showTenant ? ` from ${item.tenant_name}` : ''}? Recipients: ${(item.recipients || []).join(', ') || '(none)'}. This cannot be undone.`;
    if (!window.confirm(warn)) return;
    const key = `${item.kind}:${item.schedule_id}`;
    setBusy(b => ({ ...b, [key]: 'delete' }));
    try {
      await deleteFn(item.kind, item.schedule_id);
      toast.success(item.kind === 'briefing' ? 'Briefing turned off' : `Deleted "${item.name}"`);
      await load();
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    } finally {
      setBusy(b => { const c = { ...b }; delete c[key]; return c; });
    }
  };

  const onEditSave = async (item, patch) => {
    await editFn(item.kind, item.schedule_id, patch);
    toast.success('Schedule updated');
    await load();
  };

  const items = useMemo(() => data?.items || [], [data]);
  const countsByKind = data?.counts_by_kind || {};
  const enabledCount = useMemo(() => items.filter(i => i.enabled).length, [items]);

  if (forbidden) {
    return (
      <div className="p-6" data-testid={`${testidPrefix}-forbidden`}>
        <div className="max-w-md mx-auto mt-16 rounded-2xl border border-border bg-card p-8 text-center">
          <ShieldAlert className="w-10 h-10 mx-auto mb-3 text-amber-500" />
          <h2 className="font-heading text-lg font-semibold text-foreground">Administrator access required</h2>
          <p className="text-sm text-muted-foreground mt-2">You don’t have permission to manage schedules for this workspace. Ask a tenant administrator for access.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5" data-testid={`${testidPrefix}-page`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-semibold text-foreground flex flex-wrap items-center gap-2">
            <Icon className="w-6 h-6 text-blue-500" />
            {title}
            <Badge variant="secondary" className="text-[10px]" data-testid={`${testidPrefix}-total`}>
              {data?.total ?? 0} total · {enabledCount} enabled
            </Badge>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{description}</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="rounded-xl self-start shrink-0" data-testid={`${testidPrefix}-refresh-btn`}>
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Kind counts */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {KIND_ORDER.map(k => {
          const meta = KIND_META[k];
          const { Icon: KIcon } = meta;
          return (
            <div key={k} className="rounded-xl border border-border bg-card p-4" data-testid={`${testidPrefix}-count-${k}`}>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                <KIcon className="w-3 h-3" /> {meta.label}
              </div>
              <div className="text-2xl font-semibold text-foreground mt-1">{countsByKind[k] ?? 0}</div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="rounded-2xl border border-border bg-card p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid={`${testidPrefix}-search`}
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') load(); }}
            placeholder="Search by recipient email, name, or resource id…"
            className="pl-9 bg-background border-border text-foreground rounded-xl"
          />
        </div>
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="w-[170px] bg-background border-border text-foreground rounded-xl" data-testid={`${testidPrefix}-kind-filter`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All kinds</SelectItem>
            {KIND_ORDER.map(k => <SelectItem key={k} value={k} data-testid={`${testidPrefix}-kind-${k}`}>{KIND_META[k].label}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer" data-testid={`${testidPrefix}-enabled-only`}>
          <input type="checkbox" checked={enabledOnly} onChange={e => setEnabledOnly(e.target.checked)} className="accent-blue-500" />
          Enabled only
        </label>
        <Button size="sm" onClick={load} className="rounded-xl bg-blue-500 hover:bg-blue-600 text-white" data-testid={`${testidPrefix}-apply-btn`}>
          Apply
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground" data-testid={`${testidPrefix}-loading`}>
            <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />Loading schedules…
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground" data-testid={`${testidPrefix}-empty`}>
            No schedules match your filters.
            {q && <p className="text-xs mt-2 opacity-70">Try clearing the search to see everything.</p>}
          </div>
        ) : (
          <div className="divide-y divide-border overflow-x-auto">
            <div className={`grid ${GRID} gap-3 px-5 py-2 text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/40 min-w-[760px]`}>
              <span></span>
              <span>{showTenant ? 'Name · Tenant' : 'Name · Owner'}</span>
              <span>Kind</span>
              <span>Cadence</span>
              <span>Last run</span>
              <span>Actions</span>
            </div>
            {items.map(item => {
              const key = `${item.kind}:${item.schedule_id}`;
              const isOpen = !!expanded[key];
              const isBusyToggle = busy[key] === 'toggle';
              const isBusyDelete = busy[key] === 'delete';
              return (
                <div key={key} data-testid={`${testidPrefix}-row-${key}`} className="min-w-[760px]">
                  <div className={`grid ${GRID} gap-3 px-5 py-3 items-center hover:bg-muted/30`}>
                    <button onClick={() => setExpanded(e => ({ ...e, [key]: !isOpen }))} className="text-muted-foreground hover:text-foreground" data-testid={`${testidPrefix}-expand-${key}`}>
                      {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${item.enabled ? 'bg-emerald-500' : 'bg-muted-foreground/40'}`} />
                        <span className="text-sm text-foreground truncate">{item.name}</span>
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate flex items-center gap-1 mt-0.5">
                        <Building2 className="w-3 h-3" />
                        {showTenant ? item.tenant_name : (item.created_by || '—')}
                        {showTenant && item.tenant_plan && <span className="text-[9px] px-1 py-0.5 rounded bg-muted border border-border ml-1">{item.tenant_plan}</span>}
                      </div>
                    </div>
                    <KindBadge kind={item.kind} />
                    <span className="text-[11px] text-muted-foreground font-mono truncate" title={item.frequency}>{item.frequency}</span>
                    <span className="text-[11px] text-muted-foreground truncate" data-testid={`${testidPrefix}-lastrun-${key}`}>{fmtTime(item.last_run)}</span>
                    <div className="flex items-center gap-1.5">
                      <Button size="sm" variant="outline"
                        onClick={() => setEditing(item)}
                        disabled={isBusyToggle || isBusyDelete || item.editable === false}
                        className="rounded-lg h-7 px-2 text-[11px]"
                        data-testid={`${testidPrefix}-edit-${key}`}
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="outline"
                        onClick={() => onToggle(item)}
                        disabled={isBusyToggle || isBusyDelete}
                        className={`rounded-lg h-7 px-2 text-[11px] ${item.enabled ? 'text-amber-600 dark:text-amber-300 border-amber-500/40 hover:bg-amber-500/10' : 'text-emerald-600 dark:text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/10'}`}
                        data-testid={`${testidPrefix}-toggle-${key}`}
                      >
                        {isBusyToggle ? <Loader2 className="w-3 h-3 animate-spin" /> : <Power className="w-3 h-3 mr-1" />}
                        {item.enabled ? 'Disable' : 'Enable'}
                      </Button>
                      <Button size="sm" variant="outline"
                        onClick={() => onDelete(item)}
                        disabled={isBusyToggle || isBusyDelete}
                        className="rounded-lg h-7 px-2 text-[11px] text-rose-600 dark:text-rose-300 border-rose-500/40 hover:bg-rose-500/10"
                        data-testid={`${testidPrefix}-delete-${key}`}
                      >
                        {isBusyDelete ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                      </Button>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="px-5 pb-4 pt-1 bg-muted/20 text-xs" data-testid={`${testidPrefix}-expanded-${key}`}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-foreground">
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1"><Mail className="w-3 h-3" /> Recipients ({(item.recipients || []).length})</div>
                          {(item.recipients || []).length === 0
                            ? <span className="text-muted-foreground italic">(no recipients)</span>
                            : <div className="flex flex-wrap gap-1">{item.recipients.map(r => <span key={r} className="px-1.5 py-0.5 rounded bg-muted border border-border font-mono text-[11px]">{r}</span>)}</div>}
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Details</div>
                          <div className="font-mono text-[11px] text-muted-foreground space-y-0.5">
                            <div>id: <span className="text-foreground">{item.schedule_id}</span></div>
                            <div>resource: <span className="text-foreground">{item.resource_type || '—'}{item.resource_id ? ` / ${item.resource_id}` : ''}</span></div>
                            <div>format: <span className="text-foreground">{item.format}</span></div>
                            <div>next run: <span className="text-foreground">{fmtTime(item.next_run)}</span></div>
                            <div>created: <span className="text-foreground">{fmtTime(item.created_at)}</span> by <span className="text-foreground">{item.created_by || '—'}</span></div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-3 text-[11px]">
                        {item.enabled
                          ? <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-300"><CheckCircle2 className="w-3 h-3" /> enabled</span>
                          : <span className="inline-flex items-center gap-1 text-muted-foreground"><XCircle className="w-3 h-3" /> disabled</span>}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editing && <EditScheduleDialog item={editing} onClose={() => setEditing(null)} onSave={onEditSave} />}
    </div>
  );
}
